import React, { useState, useEffect } from 'react';
import {
  ComplianceObligation,
  ComplianceObligationInstance,
  ObligationsDashboardMetrics,
  WaiverRequestType,
} from './types';
import { obligationsService } from './obligationsService';
import { ObligationsMetricsCards } from './ObligationsMetricsCards';
import { ObligationMasterTable } from './ObligationMasterTable';
import { ObligationInstanceTable } from './ObligationInstanceTable';
import { ObligationInstanceDetailModal } from './ObligationInstanceDetailModal';

export const ObligationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'METRICS' | 'MASTERS' | 'INSTANCES'>('METRICS');
  const [metrics, setMetrics] = useState<ObligationsDashboardMetrics | null>(null);
  const [obligations, setObligations] = useState<ComplianceObligation[]>([]);
  const [instances, setInstances] = useState<ComplianceObligationInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<ComplianceObligationInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [m, o, insts] = await Promise.all([
        obligationsService.getMetrics(),
        obligationsService.getObligations(),
        obligationsService.getInstances(),
      ]);
      setMetrics(m);
      setObligations(o);
      setInstances(insts);
    } catch (err: any) {
      setError(err.message || 'Veriler yuklenirken bir hata olustu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateObligation = async (data: Partial<ComplianceObligation>) => {
    try {
      await obligationsService.createObligation(data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCreateInstance = async (data: Partial<ComplianceObligationInstance>) => {
    try {
      await obligationsService.createInstance(data);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSelectInstance = async (inst: ComplianceObligationInstance) => {
    try {
      const detailed = await obligationsService.getInstanceById(inst.id);
      setSelectedInstance(detailed);
    } catch (err) {
      setSelectedInstance(inst);
    }
  };

  const handleFulfill = async (notes: string, completedBy: string) => {
    if (!selectedInstance) return;
    try {
      await obligationsService.fulfillInstance(selectedInstance.id, notes, completedBy);
      const updated = await obligationsService.getInstanceById(selectedInstance.id);
      setSelectedInstance(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleVerify = async (data: {
    verification_status: 'PASSED' | 'FAILED' | 'PARTIAL';
    verifier_id: string;
    verifier_name: string;
    verification_notes: string;
  }) => {
    if (!selectedInstance) return;
    try {
      await obligationsService.verifyInstance(selectedInstance.id, data);
      const updated = await obligationsService.getInstanceById(selectedInstance.id);
      setSelectedInstance(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddEvidence = async (data: {
    title: string;
    file_name: string;
    file_type: string;
    uploaded_by: string;
    notes?: string;
  }) => {
    if (!selectedInstance) return;
    try {
      await obligationsService.addEvidence(selectedInstance.id, data);
      const updated = await obligationsService.getInstanceById(selectedInstance.id);
      setSelectedInstance(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRequestWaiver = async (data: {
    request_type: WaiverRequestType;
    requested_due_date?: string;
    justification: string;
    compensating_controls?: string;
    requested_by: string;
  }) => {
    if (!selectedInstance) return;
    try {
      await obligationsService.requestWaiver(selectedInstance.id, data);
      const updated = await obligationsService.getInstanceById(selectedInstance.id);
      setSelectedInstance(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleApproveWaiver = async (waiverId: string, approvedBy: string, notes?: string) => {
    if (!selectedInstance) return;
    try {
      await obligationsService.approveWaiver(waiverId, approvedBy, notes);
      const updated = await obligationsService.getInstanceById(selectedInstance.id);
      setSelectedInstance(updated);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="page-header-banner">
        <div className="badge">REGULATORY & COMPLIANCE</div>
        <h1>Compliance Obligations & Action Cycles</h1>
        <p>Operational obligation tracking, recurring compliance verification cycles, proof evidence, and exemption registry.</p>
      </div>

      {/* Global Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('METRICS')}
          className={`tab-btn ${activeTab === 'METRICS' ? 'active' : ''}`}
        >
          Summary & Metrics
        </button>
        <button
          onClick={() => setActiveTab('MASTERS')}
          className={`tab-btn ${activeTab === 'MASTERS' ? 'active' : ''}`}
        >
          Master Obligations ({obligations.length})
        </button>
        <button
          onClick={() => setActiveTab('INSTANCES')}
          className={`tab-btn ${activeTab === 'INSTANCES' ? 'active' : ''}`}
        >
          Fulfillment Cycles ({instances.length})
        </button>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl">
          ⚠️ {error}
        </div>
      )}

      {/* Main Tab Content */}
      {activeTab === 'METRICS' && (
        <ObligationsMetricsCards metrics={metrics} loading={loading} />
      )}

      {activeTab === 'MASTERS' && (
        <ObligationMasterTable
          obligations={obligations}
          onSelectObligation={(obl) => {
            setActiveTab('INSTANCES');
          }}
          onCreateObligation={handleCreateObligation}
        />
      )}

      {activeTab === 'INSTANCES' && (
        <ObligationInstanceTable
          instances={instances}
          onSelectInstance={handleSelectInstance}
          onCreateInstance={handleCreateInstance}
        />
      )}

      {/* Detailed Modal */}
      {selectedInstance && (
        <ObligationInstanceDetailModal
          instance={selectedInstance}
          onClose={() => setSelectedInstance(null)}
          onFulfill={handleFulfill}
          onVerify={handleVerify}
          onAddEvidence={handleAddEvidence}
          onRequestWaiver={handleRequestWaiver}
          onApproveWaiver={handleApproveWaiver}
        />
      )}
    </div>
  );
};
